// A node's folder (files and notes), the parts with no DOM: the icon's look, sizes, the checks the
// server makes again (shared domain/files.ts), and what an upload's failure says.
import { fileNameOf, fileNameProblem, fileSizeProblem, type FolderFile, type FolderSummary } from '@pathonline/shared';
import { hubDateTime, plural } from '../format.ts';

/**
 * The folder icon on a bar, a row or a grant card:
 *   'full'   amber: it holds files or notes
 *   'empty'  faint and dashed: nothing yet, the caller may add (not while the site is read-only)
 *   null     no icon: the caller may not open this folder (no key in the bundle's `folders`)
 */
export type FolderLook = 'full' | 'empty' | null;

export function folderLook(f: FolderSummary | undefined, readOnly: boolean): FolderLook {
  if (!f) return null;
  if (f.files > 0 || f.notes) return 'full';
  return readOnly ? null : 'empty';
}

/** "2 files and notes", "1 file", "notes", "empty". */
export function folderText(f: FolderSummary): string {
  const parts = [f.files > 0 ? plural(f.files, 'file') : '', f.notes ? 'notes' : ''].filter(Boolean);
  return parts.length ? parts.join(' and ') : 'empty';
}

/** "820 B", "4.2 KB", "36 KB", "1.5 MB", "120 MB", "1.2 GB". */
export function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  // One decimal under 10 ("4.2 KB"), none above ("36 KB"); never "10.0".
  const shown = v < 9.95 ? v.toFixed(1) : String(Math.round(v));
  return `${shown} ${units[u]}`;
}

/**
 * "2.0 KB · Ona Testaitė · 2026-10-01 11:00": a file's size, who sent it and when (the list, the preview).
 * No uploader (a shared link's files): "2.0 KB · 2026-10-01 11:00".
 */
export const fileMeta = (f: FolderFile): string => [fmtSize(f.size), f.by, hubDateTime(f.at)].filter(Boolean).join(' · ');

/** "PDF", "DOCX": the extension on the file's badge, at most 4 letters; '' without one. */
export function extOf(name: string): string {
  const m = /\.([a-z0-9]{1,8})$/i.exec(name);
  return m ? m[1]!.slice(0, 4).toUpperCase() : '';
}

/**
 * Why a file cannot be sent, '' when it can: the server's checks (shared domain/files.ts), made here first
 * so a wrong file fails at once, not after it was queued.
 */
export const fileProblem = (name: string, size: number, maxMb: number): string =>
  fileNameProblem(fileNameOf(name)) || fileSizeProblem(size, maxMb);

/** What a drop holds, as much of DataTransfer as `droppedFiles` reads. */
export interface Dropped {
  files?: ArrayLike<File> | null;
  items?: ArrayLike<{ kind: string; getAsFile(): File | null; webkitGetAsEntry?(): { isDirectory: boolean } | null }> | null;
}

/**
 * The files of a drop, and how many folders it held. A dropped folder comes as a File too, one the
 * browser cannot read: its PUT would always fail. Without `items` (an old browser): every file.
 */
export function droppedFiles(d: Dropped): { files: File[]; folders: number } {
  const items = Array.from(d.items ?? []).filter((i) => i.kind === 'file');
  if (!items.length) return { files: Array.from(d.files ?? []), folders: 0 };
  const files: File[] = [];
  let folders = 0;
  for (const i of items) {
    if (i.webkitGetAsEntry?.()?.isDirectory) folders++;
    else {
      const f = i.getAsFile();
      if (f) files.push(f);
    }
  }
  return { files, folders };
}

/** Newest first, as the server sends them; an upload added to the list goes on top. */
export const newestFirst = (files: readonly FolderFile[]) => [...files].sort((a, b) => b.at.localeCompare(a.at));

const enc = encodeURIComponent;

/** Where a folder's file links point: a member's routes (nodeLinks), or a shared link's (sharedLinks). */
export interface FileLinks {
  /** One file: shown in a tab, or `download` (fileUrl). */
  file(fileId: string, download?: boolean): string;
  /** Its read URL for the preview: GET → FileUrlAnswer. */
  url(fileId: string): string;
}

/** The file links under `base` (…/files): `<base>/<id>`, `?download=1`, `/url`. */
const linksUnder = (base: string): FileLinks => ({
  file: (fileId, download = false) => `${base}/${enc(fileId)}${download ? '?download=1' : ''}`,
  url: (fileId) => `${base}/${enc(fileId)}/url`,
});

/** A member's: /api/nodes/T-012/files/<id>. */
export const nodeLinks = (nodeId: string): FileLinks => linksUnder(`/api/nodes/${enc(nodeId)}/files`);

/** The address of one file: shown in a tab, or `download`. Plain links: the browser follows the 302 itself. */
export function fileUrl(nodeId: string, fileId: string, download = false): string {
  return nodeLinks(nodeId).file(fileId, download);
}

/** A shared folder link's folder, GET → PublicFolder: anyone with the key, no sign-in. */
export const sharedFolderUrl = (nodeId: string, key: string) => `/api/public/folders/${enc(nodeId)}/${enc(key)}`;

/** A shared folder link's: /api/public/folders/T-012/<key>/files/<id>. */
export const sharedLinks = (nodeId: string, key: string): FileLinks => linksUnder(`${sharedFolderUrl(nodeId, key)}/files`);

/** An open topic and its full description, GET → PublicTopicPage: anyone, no sign-in. */
export const publicTopicUrl = (nodeId: string) => `/api/public/topics/${enc(nodeId)}`;

/** The file chosen as an open topic's full description: /api/public/topics/T-107/files/<id>. Only that one answers. */
export const topicLinks = (nodeId: string): FileLinks => linksUnder(`${publicTopicUrl(nodeId)}/files`);

/** Where a folder chooses its topic's full description: PUT {fileId}, '' for none. */
export const describeUrl = (nodeId: string) => `/api/nodes/${enc(nodeId)}/folder/description`;

/** What a failed PUT to Blob Storage says. 0: no answer (the connection, or a CORS refusal). */
export function putProblem(status: number): string {
  if (status === 0) return 'The upload was cut off. Check the connection and try again.';
  if (status === 403) return 'The upload link was refused or has expired. Try again.';
  if (status === 413) return 'The storage refused a file this large.';
  return `The upload failed (HTTP ${status}). Try again.`;
}

/** What a failed read from Blob Storage says (the preview). 0: no answer (the connection, or the storage refusing this site). */
export function readProblem(status: number): string {
  if (status === 0) return 'The storage did not answer. Check the connection and try again.';
  if (status === 404) return 'The file is not in storage any more.';
  return `The file could not be read (HTTP ${status}).`;
}
